// =============================================================================
// MEMORY SESSION VIEW
// =============================================================================
// Split-screen overlay for shared photo slideshow during video calls
// Designed for dementia-friendly family interaction

import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorySessionStore } from '../../stores/memorySessionStore.js';
import { useCallStore } from '../../stores/callStore.js';
import { useAuthStore } from '../../stores/authStore.js';
import { MemoryPhotoDisplay } from './MemoryPhotoDisplay.js';
import { MemoryControls } from './MemoryControls.js';
import { MemoryThumbnailStrip } from './MemoryThumbnailStrip.js';

// =============================================================================
// COMPONENT
// =============================================================================

export function MemorySessionView() {
  const { t } = useTranslation();

  // Call state
  const { activeCall } = useCallStore();

  // Auth state for Comfort Mode detection. Mirrors App.tsx: both the
  // "simplified" (Comfort Mode) and "accessibility" tiers count as comfort.
  const { user } = useAuthStore();
  const isComfortMode = user?.uiMode === 'simplified' || user?.uiMode === 'accessibility';

  // Memory session state
  const {
    session,
    photos,
    displayConfig,
    nextPhoto,
    previousPhoto,
    goToPhoto,
    toggleAutoPlay,
    requestControl,
    endSession,
    updateDisplayConfig,
  } = useMemorySessionStore();

  // Who is driving the slideshow. The controller/presence data arrives over the
  // socket on every memory:updated / memory:control-granted; surface it so both
  // sides can see who holds control and a follower can take over.
  const currentUserId = user?.id ?? null;
  const controller = session?.participants.find((p) => p.isController) ?? null;
  const isController = controller?.userId === currentUserId;

  // Refs for video elements
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  // Attach local stream
  useEffect(() => {
    if (activeCall?.localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = activeCall.localStream;
    }
  }, [activeCall?.localStream]);

  // Attach remote stream
  useEffect(() => {
    if (activeCall?.remoteStreams && remoteVideoRef.current) {
      const [firstStream] = activeCall.remoteStreams.values();
      if (firstStream) {
        remoteVideoRef.current.srcObject = firstStream;
      }
    }
  }, [activeCall?.remoteStreams]);

  // Comfort Mode: apply accessibility-optimised display config when session starts
  useEffect(() => {
    if (!session?.sessionId) return;
    if (isComfortMode) {
      updateDisplayConfig({
        videoRatio: 0.25,        // smaller video, bigger photo for Mamó
        showThumbnailStrip: false, // simpler - strip is distracting
        controlSize: 'large',
        largeText: true,
        photoFit: 'contain',
        showProgressBar: false,  // less visual noise
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);

  // Auto-play is driver-authoritative: the server owns the advancing timer and
  // broadcasts each step as memory:updated, so every client renders the same
  // index at the same moment. Clients no longer run their own timer (that was
  // the sync race - two independent intervals double-advanced). The non-driver
  // simply follows the broadcast index.

  // Keyboard controls
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!session) return;

      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextPhoto();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousPhoto();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          toggleAutoPlay();
          break;
        case 'Escape':
          e.preventDefault();
          endSession();
          break;
      }
    },
    [session, nextPhoto, previousPhoto, toggleAutoPlay, endSession]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [handleKeyDown]);

  // If no active session, don't render
  if (!session || !activeCall) {
    return null;
  }

  const currentPhoto = photos[session.currentIndex];
  const hasRemoteStream = activeCall.remoteStreams.size > 0;
  const remoteParticipant = activeCall.participants[0];

  // Calculate layout ratios
  const videoWidthPercent = Math.round(displayConfig.videoRatio * 100);
  const photoWidthPercent = 100 - videoWidthPercent;

  return (
    <div className="fixed inset-0 z-50 flex bg-ink-1">
      {/* Video section (left side) */}
      <div
        className="relative flex flex-col bg-bogwood-900"
        style={{ width: `${videoWidthPercent}%` }}
      >
        {/* Remote video */}
        <div className="relative flex-1">
          {hasRemoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-press text-4xl font-bold text-white shadow-xl">
                {remoteParticipant?.displayName?.charAt(0).toUpperCase() || '?'}
              </div>
              <p className="text-lg font-medium text-white">
                {remoteParticipant?.displayName || t('calls.connecting', 'Connecting...')}
              </p>
            </div>
          )}

          {/* Local video (PiP) */}
          <div className="absolute right-2 top-2 h-24 w-20 overflow-hidden rounded-lg bg-bogwood-700 shadow-lg">
            {activeCall.isLocalVideoEnabled && activeCall.localStream ? (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-bogwood-700">
                <span className="text-xl">📷</span>
              </div>
            )}
          </div>
        </div>

        {/* Speaking indicator */}
        {remoteParticipant?.isSpeaking && (
          <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-ink-1/80 px-3 py-1.5 text-primary">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
            <span className="text-sm">{t('calls.speaking', 'Speaking')}</span>
          </div>
        )}
      </div>

      {/* Photo section (right side) */}
      <div
        className="relative flex flex-col bg-ink-1"
        style={{ width: `${photoWidthPercent}%` }}
      >
        {/* Main photo display - gentle Ken Burns drift re-triggered per photo
            via the index key. Disabled in Comfort Mode and for users who prefer
            reduced motion (handled by the .memory-kenburns media query below),
            and only applied to a 'contain' fit so nothing important is cropped. */}
        <div className="relative flex-1 overflow-hidden">
          {currentPhoto ? (
            <div
              key={session.currentIndex}
              className={
                !isComfortMode && displayConfig.photoFit === 'cover'
                  ? 'memory-kenburns h-full w-full'
                  : 'h-full w-full'
              }
            >
              <MemoryPhotoDisplay
                photo={currentPhoto}
                fit={displayConfig.photoFit}
                showInfo={displayConfig.showPhotoInfo}
                largeText={displayConfig.largeText}
              />
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-linen-300">{t('memory.noPhotos', 'No photos')}</p>
            </div>
          )}

          {/* "Who's driving" presence. The controller's name is shown to both
              sides; the follower gets a one-tap "Take control" to drive. */}
          {controller && (
            <div className="absolute left-3 top-3 z-20 flex items-center gap-2">
              <span className="flex items-center gap-1.5 rounded-full bg-ink-1/75 px-3 py-1.5 text-sm text-linen-100 backdrop-blur-sm">
                <span className="inline-block h-2 w-2 rounded-full bg-primary" />
                {isController
                  ? t('memory.youAreDriving', 'You are driving')
                  : t('memory.isDriving', '{{name}} is driving', {
                      name: controller.displayName,
                    })}
              </span>
              {!isController && (
                <button
                  onClick={requestControl}
                  className="flex min-h-[44px] items-center rounded-full bg-primary/90 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                >
                  {t('memory.takeControl', 'Take control')}
                </button>
              )}
            </div>
          )}

          {/* Progress bar */}
          {displayConfig.showProgressBar && photos.length > 0 && (
            <div className="absolute left-0 right-0 top-0 h-1 bg-bogwood-700">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((session.currentIndex + 1) / photos.length) * 100}%` }}
              />
            </div>
          )}

          {/* Tap zones - left 40% = previous, right 40% = next */}
          <button
            onClick={previousPhoto}
            className="absolute left-0 top-0 z-10 h-full w-2/5"
            aria-label={t('memory.previousPhoto', 'Previous photo')}
            style={{ background: 'transparent' }}
          />
          <button
            onClick={nextPhoto}
            className="absolute right-0 top-0 z-10 h-full w-2/5"
            aria-label={t('memory.nextPhoto', 'Next photo')}
            style={{ background: 'transparent' }}
          />

          {/* Comfort Mode: "Sit back & enjoy" auto-play prompt */}
          {isComfortMode && !session.isAutoPlaying && photos.length > 0 && (
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
              <button
                onClick={toggleAutoPlay}
                className="pointer-events-auto flex flex-col items-center gap-4 rounded-3xl bg-primary/95 px-10 py-8 text-white shadow-2xl backdrop-blur-sm transition-all hover:bg-primary active:scale-95"
                aria-label={t('memory.sitBackAndEnjoy', 'Sit back and enjoy')}
              >
                <span className="text-6xl">▶</span>
                <span className={`font-semibold tracking-wide ${displayConfig.largeText ? 'text-2xl' : 'text-xl'}`}>
                  {t('memory.sitBackAndEnjoy', 'Sit back & enjoy')}
                </span>
                <span className={`text-linen-200 ${displayConfig.largeText ? 'text-base' : 'text-sm'}`}>
                  {t('memory.autoPlayHint', 'Photos will play automatically')}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {displayConfig.showThumbnailStrip && photos.length > 1 && (
          <MemoryThumbnailStrip
            photos={photos}
            currentIndex={session.currentIndex}
            onSelect={goToPhoto}
          />
        )}

        {/* Controls */}
        <MemoryControls
          currentIndex={session.currentIndex}
          totalPhotos={photos.length}
          isAutoPlaying={session.isAutoPlaying}
          onPrevious={previousPhoto}
          onNext={nextPhoto}
          onToggleAutoPlay={toggleAutoPlay}
          onEndSession={endSession}
          controlSize={displayConfig.controlSize}
        />
      </div>

      {/* CSS for safe areas + gentle Ken Burns motion */}
      <style>{`
        .safe-area-pb {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
        .memory-kenburns {
          animation: memory-kenburns 12s ease-out both;
          transform-origin: center;
        }
        @keyframes memory-kenburns {
          from { transform: scale(1); }
          to { transform: scale(1.08); }
        }
        @media (prefers-reduced-motion: reduce) {
          .memory-kenburns { animation: none; transform: none; }
        }
      `}</style>
    </div>
  );
}
